import React, { createContext, useCallback, useContext } from 'react';

import { useMutation, useSegment } from 'utils/hooks';
import * as RequestReasonConfig from '../RequestReasonConfig';

/**
 * @type {React.Context<{
 *  onDelete: () => void;
 *  isProcessing: boolean;
 * }>}
 */
const RequestReasonDeleteContext = createContext();

export const useRequestReasonDelete = () => {
  const context = useContext(RequestReasonDeleteContext);
  if (!context) {
    throw new Error(
      'useRequestReasonDelete must be used within a RequestReasonDeleteProvider'
    );
  }
  return context;
};

export function RequestReasonDeleteProvider({
  reasonId = '',
  onDeleted = () => {},
  children = null
}) {
  const { track } = useSegment();
  const {
    onEnableForm,
    onDisableForm
  } = RequestReasonConfig.useRequestReasonConfig();

  const { isProcessing, mutate } = useMutation(
    'configure.reasons.root',
    'DELETE',
    {
      routeParam: reasonId,
      errorMessage: 'Failed to delete return reason.',
      successMessage: 'Return reason deleted successfully.',
      onValidate: () => !!reasonId,
      onSuccess: () => {
        track('FTU Wizard Return Reason Deleted', {
          reason_id: reasonId
        });
        onDeleted();
      },
      onFinally: () => onEnableForm()
    }
  );

  const onDelete = useCallback(() => {
    onDisableForm();
    mutate();
  }, [onDisableForm, mutate]);

  return (
    <RequestReasonDeleteContext.Provider
      value={{
        onDelete,
        isProcessing
      }}
    >
      {children}
    </RequestReasonDeleteContext.Provider>
  );
}
